import produce from 'immer'
import { assign, findIndex, forEach, pick, size } from 'lodash'

import { areSameValue } from '../../../core/utils'

import {
  FETCH_DISH_CURATION_DATES,
  FETCH_DISH_NAMES,
  FETCH_DISH_QA_DATES,
  FETCH_DISHES,
  FETCH_DISHES_AVAILABLE_FOR_QA_COUNTS,
  FETCH_DISHES_AVAILABLE_FOR_QC_COUNTS,
  FETCH_DISHES_PROGRESS_COUNTS,
  FETCH_DISHES_USED_METADATA_COUNTS,
  FETCH_INGREDIENT_EXPLANATIONS,
  UPDATE_DISH,
  UPDATE_DISH_AI_SUGGESTIONS,
  UPDATE_DISH_DRAWER,
  UPDATE_DISH_INGREDIENT_DISCREPANCY_ACCEPTED,
  UPDATE_DISH_INGREDIENTS_DRAWER_HOTKEYS,
  UPDATE_DISH_REMOVE_TIER_ONE_OVERRIDE_CONFIRMATION,
  UPDATE_DISH_STATUS_FLAG,
  UPDATE_DISH_TABLE_HOTKEYS,
  UPDATE_DISH_TYPE_BY_MENU_TITLE,
} from '../action-types'
import { DISHES_INITIAL_STATE } from '../initial-states'

const dishesReducer = (state = DISHES_INITIAL_STATE, action) => {
  switch (action.type) {
    case FETCH_DISHES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          data: {},
        }),
      )

    case FETCH_DISHES.COMPLETE:
      return produce(state, nextState => {
        const results = action.payload.data.dishes

        assign(nextState, {
          isLoading: false,
          error: undefined,
          data: results,
        })
      })

    case FETCH_DISHES.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: action.error.message,
          data: {},
        }),
      )

    case UPDATE_DISH.START:
      return produce(state, nextState => {
        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, action.payload.id),
        )

        assign(nextState.data?.result[index], {
          error: undefined,
          isLoading: true,
        })
      })

    case UPDATE_DISH.COMPLETE:
      return produce(state, nextState => {
        const { updateDish: dish } = action.payload.data

        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, dish.id),
        )

        assign(nextState.data?.result[index], {
          ...dish,
          isLoading: false,
          error: undefined,
        })
      })

    case UPDATE_DISH.FAIL:
      return produce(state, nextState => {
        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, action.payload.id),
        )
        assign(nextState.data?.result[index], {
          error: action.error.message,
          isLoading: false,
        })
      })

    case UPDATE_DISH_STATUS_FLAG.START:
      return produce(state, nextState => {
        if (size(action.payload.ids) === 1) {
          const index = findIndex(nextState?.data?.result, ({ id }) =>
            areSameValue(id, action.payload.ids[0]),
          )

          assign(nextState.data?.result[index], {
            isLoading: true,
            error: undefined,
            showTierOneOverrideConfirmation: false,
          })

          return nextState
        }

        nextState.error = undefined
        nextState.isLoading = true
        return nextState
      })

    case UPDATE_DISH_STATUS_FLAG.COMPLETE:
      return produce(state, nextState => {
        const { updateDishesStatusFlags: dishes } = action.payload.data

        forEach(dishes, dish => {
          const index = findIndex(nextState?.data?.result, ({ id }) =>
            areSameValue(id, dish.id),
          )

          assign(nextState.data?.result[index], {
            ...dish,
            isLoading: false,
            error: undefined,
            showTierOneOverrideConfirmation: false,
          })
        })

        if (size(action.payload.variables.ids) > 1) {
          nextState.error = undefined
          nextState.isLoading = false
        }

        return nextState
      })

    case UPDATE_DISH_STATUS_FLAG.FAIL:
      return produce(state, nextState => {
        if (size(action.payload.ids) === 1) {
          const index = findIndex(nextState?.data?.result, ({ id }) =>
            areSameValue(id, action.payload.ids[0]),
          )

          const showTierOneOverrideConfirmation =
            action.error?.graphQLErrors?.[0]?.extensions
              ?.showTierOneOverrideConfirmation

          assign(nextState.data?.result[index], {
            isLoading: false,
            error: !showTierOneOverrideConfirmation
              ? action.error.message
              : undefined,
            showTierOneOverrideConfirmation,
          })

          return nextState
        }

        nextState.error = action.error.message
        nextState.isLoading = false
        return nextState
      })

    case UPDATE_DISH_TYPE_BY_MENU_TITLE.START:
      return produce(state, nextState => {
        assign(nextState, {
          isDishTypeMenuTitleUpdateLoading: true,
          dishTypeMenuTitleUpdateError: undefined,
        })
      })

    case UPDATE_DISH_TYPE_BY_MENU_TITLE.COMPLETE:
      return produce(state, nextState => {
        const { updateDishTypeByMenuTitle: dishes } = action.payload.data

        forEach(dishes, dish => {
          const index = findIndex(nextState?.data?.result, ({ id }) =>
            areSameValue(id, dish.id),
          )

          assign(nextState.data?.result[index], dish)
        })
        assign(nextState, {
          isDishTypeMenuTitleUpdateLoading: false,
        })
      })

    case UPDATE_DISH_TYPE_BY_MENU_TITLE.FAIL:
      return produce(state, nextState => {
        assign(nextState, {
          isDishTypeMenuTitleUpdateLoading: false,
          dishTypeMenuTitleUpdateError: action.error.message,
        })
      })

    case UPDATE_DISH_TABLE_HOTKEYS:
      return produce(state, nextState => {
        assign(nextState.hotkeys, action.payload)
      })

    case UPDATE_DISH_INGREDIENTS_DRAWER_HOTKEYS:
      return produce(state, nextState => {
        assign(nextState.drawerHotkeys, action.payload)
      })

    case UPDATE_DISH_DRAWER:
      return produce(state, nextState => {
        assign(nextState.drawer, action.payload)
      })

    case UPDATE_DISH_REMOVE_TIER_ONE_OVERRIDE_CONFIRMATION:
      return produce(state, nextState => {
        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, action.payload.id),
        )
        assign(nextState.data?.result[index], {
          showTierOneOverrideConfirmation: false,
        })
      })

    case FETCH_INGREDIENT_EXPLANATIONS.START:
      return produce(state, nextState => {
        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, action.payload.id),
        )
        assign(nextState.data?.result[index], {
          error: undefined,
          areIngredientExplanationsLoading: true,
        })
      })

    case FETCH_INGREDIENT_EXPLANATIONS.COMPLETE:
      return produce(state, nextState => {
        const { ingredientExplanations } = action.payload.data

        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, action.payload?.variables?.id),
        )

        assign(nextState.data?.result[index], {
          ingredientExplanations,
          areIngredientExplanationsLoading: false,
        })
      })

    case FETCH_INGREDIENT_EXPLANATIONS.FAIL:
      return produce(state, nextState => {
        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, action.payload.id),
        )
        assign(nextState.data?.result[index], {
          error: action.error.message,
          areIngredientExplanationsLoading: false,
        })
      })

    case UPDATE_DISH_INGREDIENT_DISCREPANCY_ACCEPTED.START:
      return produce(state, nextState => {
        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, action.payload.dishId),
        )
        assign(nextState.data?.result[index], {
          error: undefined,
          loadingDiscrepancyAcceptedIngredientId: action.payload.ingredientId,
        })
      })

    case UPDATE_DISH_INGREDIENT_DISCREPANCY_ACCEPTED.COMPLETE:
      return produce(state, nextState => {
        const { updateDishIngredientDiscrepancyAccepted } = action.payload.data

        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, action.payload?.variables?.dishId),
        )

        assign(nextState.data?.result[index], {
          ...pick(updateDishIngredientDiscrepancyAccepted, [
            'mainIngredients',
            'choiceIngredients',
          ]),
          loadingDiscrepancyAcceptedIngredientId: null,
        })
      })

    case UPDATE_DISH_INGREDIENT_DISCREPANCY_ACCEPTED.FAIL:
      return produce(state, nextState => {
        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, action.payload.dishId),
        )
        assign(nextState.data?.result[index], {
          error: action.error.message,
          loadingDiscrepancyAcceptedIngredientId: null,
        })
      })

    case UPDATE_DISH_AI_SUGGESTIONS.START:
      return produce(state, nextState => {
        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, action.payload.id),
        )
        assign(nextState.data?.result[index], {
          error: undefined,
          isLoading: true,
        })
      })

    case UPDATE_DISH_AI_SUGGESTIONS.COMPLETE:
      return produce(state, nextState => {
        const { updateDishWithManualAndAiSuggestions: dish } =
          action.payload.data

        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, dish.id),
        )

        assign(nextState.data?.result[index], {
          ...dish,
          isLoading: false,
          error: undefined,
        })
      })

    case UPDATE_DISH_AI_SUGGESTIONS.FAIL:
      return produce(state, nextState => {
        const index = findIndex(nextState?.data?.result, ({ id }) =>
          areSameValue(id, action.payload.id),
        )
        assign(nextState.data?.result[index], {
          error: action.error.message,
          isLoading: false,
        })
      })

    case FETCH_DISHES.CLEANUP:
      return { ...DISHES_INITIAL_STATE }

    case FETCH_DISH_NAMES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isNamesLoading: true,
          namesError: undefined,
          names: {},
        }),
      )

    case FETCH_DISH_NAMES.COMPLETE:
      return produce(state, nextState => {
        const { dishNames } = action.payload.data

        return assign(nextState, {
          isNamesLoading: false,
          namesError: undefined,
          names: dishNames,
        })
      })

    case FETCH_DISH_NAMES.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isNamesLoading: false,
          namesError: action.error.message,
          names: {},
        }),
      )

    case FETCH_DISHES_AVAILABLE_FOR_QA_COUNTS.START:
      return produce(state, nextState =>
        assign(nextState, {
          availableForQaCounts: [],
          isAvailableForQaCountsLoading: true,
          availableForQaCountsError: undefined,
        }),
      )

    case FETCH_DISHES_AVAILABLE_FOR_QA_COUNTS.COMPLETE:
      return produce(state, nextState => {
        const { dishesAvailableForQaCounts } = action.payload.data

        return assign(nextState, {
          availableForQaCounts: dishesAvailableForQaCounts,
          isAvailableForQaCountsLoading: false,
          availableForQaCountsError: undefined,
        })
      })

    case FETCH_DISHES_AVAILABLE_FOR_QA_COUNTS.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          availableForQaCounts: {},
          isAvailableForQaCountsLoading: false,
          availableForQaCountsError: action.error.message,
        }),
      )

    case FETCH_DISHES_PROGRESS_COUNTS.START:
      return produce(state, nextState =>
        assign(nextState, {
          isProgressCountsLoading: true,
          progressCountsError: undefined,
        }),
      )

    case FETCH_DISHES_PROGRESS_COUNTS.COMPLETE:
      return produce(state, nextState => {
        const { dishesProgressCounts } = action.payload.data

        return assign(nextState, {
          progressCounts: dishesProgressCounts,
          isProgressCountsLoading: false,
          progressCountsError: undefined,
        })
      })

    case FETCH_DISHES_PROGRESS_COUNTS.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          progressCounts: {},
          isProgressCountsLoading: false,
          progressCountsError: action.error.message,
        }),
      )

    case FETCH_DISH_CURATION_DATES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isCurationDatesLoading: true,
          curationDatesError: undefined,
          curationDates: [],
        }),
      )

    case FETCH_DISH_CURATION_DATES.COMPLETE:
      return produce(state, nextState => {
        const { dishCurationDatesByCurator } = action.payload.data

        return assign(nextState, {
          isCurationDatesLoading: false,
          curationDatesError: undefined,
          curationDates: dishCurationDatesByCurator,
        })
      })

    case FETCH_DISH_CURATION_DATES.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isCurationDatesLoading: false,
          curationDatesError: action.error.message,
          curationDates: [],
        }),
      )

    case FETCH_DISHES_AVAILABLE_FOR_QC_COUNTS.START:
      return produce(state, nextState =>
        assign(nextState, {
          availableForQcCounts: [],
          isAvailableForQcCountsLoading: true,
          availableForQcCountsError: undefined,
        }),
      )

    case FETCH_DISHES_AVAILABLE_FOR_QC_COUNTS.COMPLETE:
      return produce(state, nextState => {
        const { dishesAvailableForQcCounts } = action.payload.data

        return assign(nextState, {
          availableForQcCounts: dishesAvailableForQcCounts,
          isAvailableForQcCountsLoading: false,
          availableForQcCountsError: undefined,
        })
      })

    case FETCH_DISHES_AVAILABLE_FOR_QC_COUNTS.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          availableForQcCounts: [],
          isAvailableForQcCountsLoading: false,
          availableForQcCountsError: action.error.message,
        }),
      )

    case FETCH_DISHES_USED_METADATA_COUNTS.START:
      return produce(state, nextState =>
        assign(nextState, {
          usedMetadataCounts: {},
          isUsedMetadataCountsLoading: true,
          usedMetadataCountsError: undefined,
        }),
      )

    case FETCH_DISHES_USED_METADATA_COUNTS.COMPLETE:
      return produce(state, nextState => {
        const { dishesUsedMetadataCounts } = action.payload.data

        return assign(nextState, {
          usedMetadataCounts: dishesUsedMetadataCounts,
          isUsedMetadataCountsLoading: false,
          usedMetadataCountsError: undefined,
        })
      })

    case FETCH_DISHES_USED_METADATA_COUNTS.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          usedMetadataCounts: {},
          isUsedMetadataCountsLoading: false,
          usedMetadataCountsError: action.error.message,
        }),
      )

    case FETCH_DISH_QA_DATES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isQaDatesLoading: true,
          qaDatesError: undefined,
          qaDates: [],
        }),
      )

    case FETCH_DISH_QA_DATES.COMPLETE:
      return produce(state, nextState => {
        const { dishQaDatesByQa } = action.payload.data

        return assign(nextState, {
          isQaDatesLoading: false,
          qaDatesError: undefined,
          qaDates: dishQaDatesByQa,
        })
      })

    case FETCH_DISH_QA_DATES.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isQaDatesLoading: false,
          qaDatesError: action.error.message,
          qaDates: [],
        }),
      )
    default:
      return state
  }
}

export default dishesReducer
